import React, { memo, useCallback, useMemo } from 'react';

import { Box, Checkbox, HStack, Text } from '@chakra-ui/react';

import AppTable, { createColumns } from 'components/AppTable';

import { getCurrentUnitPrice, getOriginalUnitPrice } from './bulkEditState';
import EditablePriceCell from './EditablePriceCell';
import { BudgetInputPrice, EditedValues } from './types';

type InputsTableProps = {
  data: BudgetInputPrice[];
  selectedIds: number[];
  editedValues: EditedValues;
  invalidModifiedIds: number[];
  loading: boolean;
  onToggleSelection: (id: number) => void;
  onToggleSelectAllCurrentPage: () => void;
  onUpdateSort: (sort: string) => void;
  onEditUnitPrice: (
    item: BudgetInputPrice,
    value: number | null | undefined,
  ) => void;
  onUndoItemChanges: (id: number) => void;
};

type SelectionCellProps = {
  id: number;
  code?: string | null;
  isSelected: boolean;
  onToggleSelection: (id: number) => void;
};

const SelectionCell = memo<SelectionCellProps>(
  ({ id, code, isSelected, onToggleSelection }) => {
    const handleToggleSelection = useCallback(() => {
      onToggleSelection(id);
    }, [id, onToggleSelection]);

    return (
      <Checkbox
        size="lg"
        isChecked={isSelected}
        onChange={handleToggleSelection}
        aria-label={`Selecionar insumo ${code || id}`}
      />
    );
  },
);

type PriceCellProps = {
  item: BudgetInputPrice;
  value: number;
  originalValue: number;
  isModified: boolean;
  isInvalid: boolean;
  onEditUnitPrice: (
    item: BudgetInputPrice,
    value: number | null | undefined,
  ) => void;
  onUndoItemChanges: (id: number) => void;
};

const PriceCell = memo<PriceCellProps>(
  ({
    item,
    value,
    originalValue,
    isModified,
    isInvalid,
    onEditUnitPrice,
    onUndoItemChanges,
  }) => {
    const handleChange = useCallback(
      (nextValue: number) => {
        onEditUnitPrice(item, nextValue);
      },
      [item, onEditUnitPrice],
    );

    const handleUndo = useCallback(() => {
      onUndoItemChanges(item.id);
    }, [item.id, onUndoItemChanges]);

    return (
      <EditablePriceCell
        itemId={item.id}
        value={value}
        originalValue={originalValue}
        isModified={isModified}
        isInvalid={isInvalid}
        onChange={handleChange}
        onUndo={handleUndo}
      />
    );
  },
);

const InputsTable: React.FC<InputsTableProps> = ({
  data,
  selectedIds,
  editedValues,
  invalidModifiedIds,
  loading,
  onToggleSelection,
  onToggleSelectAllCurrentPage,
  onUpdateSort,
  onEditUnitPrice,
  onUndoItemChanges,
}) => {
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const invalidModifiedIdsSet = useMemo(
    () => new Set(invalidModifiedIds),
    [invalidModifiedIds],
  );

  const isAllSelected = useMemo(() => {
    return data.length > 0 && data.every((item) => selectedIdsSet.has(item.id));
  }, [data, selectedIdsSet]);

  const isPartiallySelected = useMemo(() => {
    if (selectedIds.length === 0 || isAllSelected) {
      return false;
    }

    return data.some((item) => selectedIdsSet.has(item.id));
  }, [data, isAllSelected, selectedIds.length, selectedIdsSet]);

  const columns = useMemo(() => {
    return createColumns([
      {
        field: 'selection',
        description: (
          <HStack
            mb={1}
            justifyContent={{ base: 'space-between', lg: 'center' }}
            spacing={0}
          >
            <Text
              fontSize="xs"
              display={{ base: 'inline', lg: 'none' }}
              whiteSpace="nowrap"
            >
              Selecionar
            </Text>

            <Checkbox
              size="lg"
              isChecked={isAllSelected}
              isIndeterminate={isPartiallySelected}
              onChange={onToggleSelectAllCurrentPage}
              aria-label="Selecionar todos os insumos"
              display={{ base: 'none', lg: 'inline-flex' }}
            />
          </HStack>
        ),
        sortable: false,
        isCentered: true,
      },
      {
        field: 'base',
        description: 'Base',
        sortable: false,
      },
      {
        field: 'code',
        description: 'Código',
        sortable: false,
      },
      {
        field: 'description',
        description: 'Descrição',
        sortable: false,
      },
      {
        field: 'unit_measure',
        description: 'Unidade',
        sortable: false,
        isCentered: true,
      },
      {
        field: 'total',
        description: 'Valor unitário',
        isNumeric: true,
      },
    ]);
  }, [isAllSelected, isPartiallySelected, onToggleSelectAllCurrentPage]);

  const mapInput = useCallback(
    (item: BudgetInputPrice) => {
      const isModified = Object.prototype.hasOwnProperty.call(
        editedValues,
        item.id,
      );
      const currentValue = getCurrentUnitPrice(item, editedValues);
      const originalValue = getOriginalUnitPrice(item);
      const isInvalid = invalidModifiedIdsSet.has(item.id);
      const isSelected = selectedIdsSet.has(item.id);

      return {
        id: item.id,
        selected: isSelected,
        active: isModified,
        selection: (
          <SelectionCell
            id={item.id}
            code={item.input?.code}
            isSelected={isSelected}
            onToggleSelection={onToggleSelection}
          />
        ),
        base:
          item.base?.description ||
          item.input?.version?.base?.description ||
          '-',
        code: item.input?.code || '-',
        description: (
          <Text
            py={1}
            maxWidth={{ base: '100%', md: '650px' }}
            whiteSpace="pre-wrap"
          >
            {item.custom_description || item.input?.description || '-'}
          </Text>
        ),
        unit_measure:
          item.unit_measure?.description ||
          item.input?.unit_measure?.description ||
          '-',
        total: (
          <PriceCell
            item={item}
            value={currentValue}
            originalValue={originalValue}
            isModified={isModified}
            isInvalid={isInvalid}
            onEditUnitPrice={onEditUnitPrice}
            onUndoItemChanges={onUndoItemChanges}
          />
        ),
        actions: null,
      };
    },
    [
      editedValues,
      invalidModifiedIdsSet,
      onEditUnitPrice,
      onToggleSelection,
      onUndoItemChanges,
      selectedIdsSet,
    ],
  );

  return (
    <Box overflowX="auto">
      <AppTable
        cols={columns}
        mapping={mapInput}
        data={data}
        loading={loading}
        noDataMessage="Nenhum insumo encontrado."
        onUpdateSort={onUpdateSort}
      />
    </Box>
  );
};

export default InputsTable;
